import React, { useEffect, useId, useRef, useState } from "react";
import { Check, FileDown, Globe2, Link2, Loader2, Share } from "lucide-react";
import { DebatePDFButtonProps, useDebatePdf } from "./DebatePDFButton";
import { ShareLinkDialog } from "./ShareLinkButton";
import { FeedPostDialog } from "./FeedPostButton";
import { ShareLinkState } from "../hooks/useShareLink";
import { FeedPostState } from "../hooks/useFeedPost";
import { FEED_ENABLED } from "../utils/features";

interface MenuItemProps {
  icon: React.ReactNode;
  title: string;
  detail: string;
  onSelect: (item: HTMLButtonElement) => void;
  disabled?: boolean;
}

const MENU_ROW_CLASS = "grid w-full grid-cols-[16px_minmax(0,1fr)] items-start gap-x-3 rounded-md px-2.5 py-2 text-left hover:bg-hover active:bg-pressed focus:outline-none focus-visible:bg-hover focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-60 transition-colors duration-100";

function menuItems(menu: HTMLElement | null): HTMLButtonElement[] {
  return Array.from(menu?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not([disabled])') ?? []);
}

function MenuItem({ icon, title, detail, onSelect, disabled }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      disabled={disabled}
      onClick={(e) => onSelect(e.currentTarget)}
      className={MENU_ROW_CLASS}
    >
      <span className="mt-0.5 text-muted" aria-hidden="true">{icon}</span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-content">{title}</span>
        <span className="block text-xs leading-5 text-muted">{detail}</span>
      </span>
    </button>
  );
}

/**
 * One share button for a finished debate. It opens a menu above itself with
 * the private link, the public feed and the PDF; link and feed need an account.
 * Posting closes the menu and runs in the background, reporting from a toast.
 */
export default function DebateShareMenu({
  pdf,
  shareLink,
  feedPost,
  debateId,
  signedIn,
}: {
  pdf: DebatePDFButtonProps;
  shareLink: ShareLinkState;
  feedPost: FeedPostState;
  /** Null when the debate can't be shared online (not saved, or no final answer). */
  debateId: string | null;
  signedIn: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<"link" | "feed" | null>(null);
  const [anonymous, setAnonymous] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const { status: pdfStatus, generate, label: pdfLabel } = useDebatePdf(pdf);
  const online = signedIn && !!debateId;

  const items = () => menuItems(menuRef.current);

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    menuItems(menuRef.current)[0]?.focus();
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [open]);

  const { posted, busy, warm } = feedPost;
  useEffect(() => {
    if (open && online && FEED_ENABLED && !posted) warm();
  }, [open, online, posted, warm]);

  const onMenuKey = (e: React.KeyboardEvent) => {
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const move = (i: number) => { e.preventDefault(); list[(i + list.length) % list.length]?.focus(); };
    if (e.key === "ArrowDown") move(at + 1);
    else if (e.key === "ArrowUp") move(at - 1);
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(list.length - 1);
    else if (e.key === "Escape") { e.preventDefault(); close(); }
    else if (e.key === "Tab") setOpen(false);
  };

  const closeDialog = () => {
    setDialog(null);
    triggerRef.current?.focus();
  };

  const openDialog = (which: "link" | "feed") => {
    setOpen(false);
    setDialog(which);
  };

  const postToFeed = (item: HTMLButtonElement) => {
    const { left, top, width, height } = item.getBoundingClientRect();
    void feedPost.publish({ anonymous, origin: { left, top, width, height } });
    close();
  };

  const pdfTitle = pdfStatus === "pending" ? "Preparing PDF…" : pdfStatus === "success" ? "PDF downloaded" : pdfStatus === "error" ? "PDF failed, try again" : pdfLabel;
  const feedTitle = busy ? (posted ? "Removing from feed…" : "Posting to feed…") : posted ? "On the feed" : "Post to feed";
  const feedDetail = posted ? "View or remove the post" : `Public, open to comments, shows ${anonymous ? "no name" : "your first name"}`;

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp" || e.key === "ArrowDown") { e.preventDefault(); setOpen(true); }
        }}
        className={`inline-flex min-h-[48px] min-w-[64px] flex-col items-center justify-center gap-0.5 rounded-md px-3 py-1 text-content hover:bg-hover active:bg-pressed focus:outline-none focus-visible:ring-2 focus-visible:ring-focus transition-colors duration-100 ${open ? "bg-hover" : ""}`}
      >
        <Share size={20} aria-hidden="true" />
        <span className="text-xs font-medium">Share</span>
      </button>

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Share this debate"
          onKeyDown={onMenuKey}
          className="absolute bottom-full left-0 z-30 mb-2 w-72 max-w-[calc(100vw-1.5rem)] rounded-lg border border-line bg-raised p-1.5 shadow-lg"
        >
          {online && (
            <>
              <MenuItem
                icon={<Link2 size={16} />}
                title={shareLink.shareId ? "Shared link" : "Share link"}
                detail={shareLink.shareId ? "On. Copy it or turn it off" : FEED_ENABLED ? "Private link, not on the feed" : "Private link to this debate"}
                onSelect={() => openDialog("link")}
              />
              {FEED_ENABLED && (
                <MenuItem
                  icon={busy ? <Loader2 size={16} className="animate-spin" /> : <Globe2 size={16} />}
                  title={feedTitle}
                  detail={feedDetail}
                  onSelect={(item) => { if (posted) openDialog("feed"); else postToFeed(item); }}
                  disabled={busy}
                />
              )}
              {FEED_ENABLED && !posted && !busy && (
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={anonymous}
                  tabIndex={-1}
                  onClick={() => setAnonymous((v) => !v)}
                  className={MENU_ROW_CLASS}
                >
                  <span className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-sm border border-line text-content" aria-hidden="true">
                    {anonymous && <Check size={12} />}
                  </span>
                  <span className="block text-sm text-content">Post without my name</span>
                </button>
              )}
            </>
          )}
          <MenuItem
            icon={pdfStatus === "pending" ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
            title={pdfTitle}
            detail="Saves a copy to your device"
            onSelect={() => { void generate(); }}
            disabled={pdfStatus === "pending"}
          />
          {!signedIn && (
            <p className="mt-1 border-t border-line px-2.5 pb-1 pt-2 text-xs leading-5 text-muted">
              {FEED_ENABLED ? "Sign in to share a link or post to the feed." : "Sign in to share a link."}
            </p>
          )}
        </div>
      )}

      {online && (
        <>
          <ShareLinkDialog state={shareLink} open={dialog === "link"} onClose={closeDialog} />
          {FEED_ENABLED && <FeedPostDialog state={feedPost} debateId={debateId!} open={dialog === "feed"} onClose={closeDialog} />}
        </>
      )}
    </div>
  );
}
